import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SMPSDevice, SMPSStats, StationStats } from './smpsTypes';
import { getBatteryModeLabel, getStatusLabel } from './smpsConstants';

/**
 * Format date for PDF header
 */
const formatDate = (): string => {
  return new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

/**
 * Add header to PDF
 */
const addHeader = (doc: jsPDF, title: string): void => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, 20, { align: 'center' });

  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${formatDate()}`, pageWidth / 2, 28, { align: 'center' });

  // Line separator
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(14, 32, pageWidth - 14, 32);
};

/**
 * Add footer to PDF
 */
const addFooter = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text('SMPS Power Supply Monitoring System', 14, pageHeight - 10);
  }
};

/**
 * Export all SMPS devices to PDF
 */
export const exportDevicesToPdf = (
  devices: SMPSDevice[],
  stats: SMPSStats,
  stationStats: StationStats[],
  filename: string = 'smps-devices-report'
): void => {
  const doc = new jsPDF('landscape', 'mm', 'a4');

  addHeader(doc, 'SMPS Power Supply Report');

  // Summary stats
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, 40);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const summaryData = [
    `Total Devices: ${stats.totalDevices}`,
    `Healthy: ${stats.healthy} | Warning: ${stats.warning} | Critical: ${stats.critical} | Offline: ${stats.offline}`,
    `Avg Efficiency: ${stats.avgEfficiency}%`,
    `Total Load: ${stats.totalLoad}A`,
  ];
  doc.text(summaryData, 14, 48);

  // Devices table
  const tableData = devices.map((device) => [
    device.lastActivity,
    device.name,
    device.station,
    `${device.dcVoltage}V`,
    `${device.loadCurrent}A`,
    `${device.battery.capacity}%`,
    getBatteryModeLabel(device.battery.mode),
    `${device.acInput.phaseR}/${device.acInput.phaseY}/${device.acInput.phaseB}V`,
    `${device.rectifiers.online}/${device.rectifiers.total}`,
    getStatusLabel(device.status),
  ]);

  autoTable(doc, {
    head: [['Last Activity', 'Device', 'Station', 'DC Volt', 'Load', 'Battery', 'Mode', 'AC (R/Y/B)', 'Rect', 'Status']],
    body: tableData,
    startY: 65,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      8: { halign: 'center' },
      9: { halign: 'center' },
    },
    didParseCell: (data) => {
      // Color status cells
      if (data.section === 'body' && data.column.index === 9) {
        const value = data.cell.raw as string;
        if (value === 'Healthy') {
          data.cell.styles.textColor = [39, 174, 96];
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'Warning') {
          data.cell.styles.textColor = [243, 156, 18];
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'Critical' || value === 'Offline') {
          data.cell.styles.textColor = [231, 76, 60];
          data.cell.styles.fontStyle = 'bold';
        }
      }
      // Color battery mode
      if (data.section === 'body' && data.column.index === 6) {
        const value = data.cell.raw as string;
        if (value === 'Float Charging') {
          data.cell.styles.textColor = [39, 174, 96];
        } else if (value === 'Discharging') {
          data.cell.styles.textColor = [231, 76, 60];
        }
      }
    },
  });

  // Station-wise summary on new page
  if (stationStats.length > 0) {
    doc.addPage();
    addHeader(doc, 'Station-wise Summary');

    const stationData = stationStats.map((s) => [
      s.station,
      s.totalDevices.toString(),
      `${s.avgVoltage.toFixed(1)}V`,
      `${s.totalCurrent.toFixed(1)}A`,
      s.healthyCount.toString(),
      s.warningCount.toString(),
      s.criticalCount.toString(),
    ]);

    autoTable(doc, {
      head: [['Station', 'Devices', 'Avg Voltage', 'Total Current', 'Healthy', 'Warning', 'Critical']],
      body: stationData,
      startY: 40,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
  }

  addFooter(doc);
  doc.save(`${filename}.pdf`);
};

/**
 * Export single SMPS device details to PDF
 */
export const exportDeviceDetailToPdf = (device: SMPSDevice): void => {
  const doc = new jsPDF('portrait', 'mm', 'a4');

  addHeader(doc, `SMPS Device Details - ${device.name}`);

  let yPos = 45;

  // Device Info Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Device Information', 14, yPos);
  yPos += 8;

  const deviceInfo = [
    ['Device Name', device.name],
    ['Station', device.station],
    ['Last Activity', device.lastActivity],
    ['Status', getStatusLabel(device.status)],
    ['Communication', device.communicationStatus],
  ];

  autoTable(doc, {
    body: deviceInfo,
    startY: yPos,
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'plain',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Power Metrics Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Power Metrics', 14, yPos);
  yPos += 8;

  const powerData = [
    ['DC Voltage', `${device.dcVoltage}V`],
    ['Load Current', `${device.loadCurrent}A`],
    ['Efficiency', `${device.efficiency}%`],
    ['Float Voltage', `${device.floatVoltage}V`],
    ['Boost Voltage', `${device.boostVoltage}V`],
    ['Power Mode', device.powerMode],
  ];

  autoTable(doc, {
    body: powerData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Battery Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Battery Status', 14, yPos);
  yPos += 8;

  const batteryData = [
    ['Voltage', `${device.battery.voltage}V`],
    ['Current', `${device.battery.current}A`],
    ['SOC (State of Charge)', `${device.battery.capacity}%`],
    ['Mode', getBatteryModeLabel(device.battery.mode)],
    ['Capacity', `${device.battery.capacityAh} Ah`],
    ['Battery Banks', device.battery.count.toString()],
    ['BMS Units', device.battery.bmsCount.toString()],
    ['Temperature', `${device.battery.temperature}°C`],
  ];

  autoTable(doc, {
    body: batteryData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // AC Input Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('AC Input', 14, yPos);
  yPos += 8;

  const acData = [
    ['Phase R', `${device.acInput.phaseR}V`],
    ['Phase Y', `${device.acInput.phaseY}V`],
    ['Phase B', `${device.acInput.phaseB}V`],
    ['Frequency', `${device.acInput.frequency} Hz`],
    ['Status', device.acInput.status],
    ['Input Type', device.acInput.inputType],
  ];

  autoTable(doc, {
    body: acData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  // New page for more details
  doc.addPage();
  addHeader(doc, `SMPS Device Details - ${device.name} (Continued)`);
  yPos = 45;

  // Rectifiers Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Rectifiers', 14, yPos);
  yPos += 8;

  const rectData = [
    ['Total Slots', device.rectifiers.total.toString()],
    ['Online', device.rectifiers.online.toString()],
    ['Total Current', `${device.rectifiers.totalCurrent}A`],
    ['Total Power', `${(device.rectifiers.totalPower / 1000).toFixed(2)} kW`],
    ['Current Limit', `${device.rectifiers.currentLimit}A`],
  ];

  autoTable(doc, {
    body: rectData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Load Outputs Section
  if (device.loads.some(l => l.current > 0 || l.todayEnergy > 0)) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Load Outputs', 14, yPos);
    yPos += 8;

    const loadTableData = device.loads.map((load, idx) => [
      `Load ${idx + 1}`,
      `${load.current.toFixed(2)}A`,
      `${(load.todayEnergy / 1000).toFixed(2)} kWh`,
      `${(load.totalEnergy / 1000).toFixed(2)} kWh`,
    ]);

    autoTable(doc, {
      head: [['Load', 'Current', 'Today Energy', 'Total Energy']],
      body: loadTableData,
      startY: yPos,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // Thresholds Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('System Thresholds', 14, yPos);
  yPos += 8;

  const thresholdData = [
    ['DC Voltage High', `${device.thresholds.dcVoltageHigh}V`],
    ['DC Voltage Low', `${device.thresholds.dcVoltageLow}V`],
    ['AC Over Voltage', `${device.thresholds.acOverVoltage}V`],
    ['AC Under Voltage', `${device.thresholds.acUnderVoltage}V`],
    ['Battery Over Temp', `${device.thresholds.battOverTemp}°C`],
    ['Battery High Temp', `${device.thresholds.battHighTemp}°C`],
    ['Battery Low Temp', `${device.thresholds.battLowTemp}°C`],
  ];

  autoTable(doc, {
    body: thresholdData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // LVD Configuration Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('LVD Configuration', 14, yPos);
  yPos += 8;

  const lvdData = [
    ['Battery LVD', `${device.lvd.batteryLvd.enabled ? 'Enabled' : 'Disabled'} @ ${device.lvd.batteryLvd.voltage}V`],
    ...device.lvd.loadLvd.map((lvd, idx) => [
      `Load LVD ${idx + 1}`,
      `${lvd.enabled ? 'Enabled' : 'Disabled'} @ ${lvd.voltage}V`,
    ]),
  ];

  autoTable(doc, {
    body: lvdData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Asset Details Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Asset Details', 14, yPos);
  yPos += 8;

  const { assetDetails } = device;
  const assetData = [
    ['Make', assetDetails.make || 'N/A'],
    ['Model', assetDetails.model || 'N/A'],
    ['Serial Number', assetDetails.serialNumber || 'N/A'],
    ['Capacity', assetDetails.capacity || 'N/A'],
    ['IP Address', assetDetails.ipAddress || 'N/A'],
    ['Firmware', assetDetails.firmwareVersion || 'N/A'],
    ['Location', assetDetails.assetPosition || 'N/A'],
    ['Coordinates', `${assetDetails.latitude}, ${assetDetails.longitude}`],
  ];

  autoTable(doc, {
    body: assetData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 90 },
    },
    theme: 'striped',
  });

  addFooter(doc);
  doc.save(`smps-device-${device.name.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`);
};
